import axios, { AxiosError } from 'axios';
import { clearSession, getAccessToken, redirectToLogin } from '../auth/session';

const API_BASE = '/origo/api';

const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  // Note: No withCredentials needed - using Bearer token authentication
});

// Request interceptor to add Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to unwrap Fruster response format and handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Fruster services return: { status, data, reqId, transactionId, ms, from }
    // We need to extract the nested 'data' field
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      response.data = response.data.data;
    }
    return response;
  },
  (error: AxiosError<ApiErrorBody>) => {
    // Only a session we actually had can expire. Without a token the 401/403
    // is the login itself failing, or a public page calling the API, and the
    // caller shows that error.
    if (isSessionError(error) && getAccessToken()) {
      clearSession();
      redirectToLogin();
    }
    return Promise.reject(error);
  }
);

interface ApiErrorBody {
  error?: { code?: string; thrower?: string };
}

// 403s that come from the bus auth (missing token/scope), as opposed to a
// service saying no to a specific action (e.g. "Customers are not allowed to
// handle hidden jobs"), which the calling code should show to the user.
const AUTH_ERROR_CODES = ['MUST_BE_LOGGED_IN', 'PERMISSION_DENIED'];

/**
 * True when the response means the token is missing, invalid or expired.
 * oo-auth-service reports a bad token as 403 with a code like
 * `auth-service.403.1` ("Invalid JWT token" / "Token expired").
 */
export function isSessionError(error: unknown): boolean {
  if (!axios.isAxiosError<ApiErrorBody>(error)) return false;
  const status = error.response?.status;
  if (status === 401) return true;
  if (status !== 403) return false;
  const { code, thrower } = error.response?.data?.error || {};
  if (!code) return true;
  return AUTH_ERROR_CODES.includes(code) || code.startsWith('auth-service.') || thrower === 'oo-auth-service';
}

export async function apiGet<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
  const { data } = await apiClient.get<T>(endpoint, { params });
  return data;
}

export async function apiPost<T>(endpoint: string, body: any): Promise<T> {
  const { data } = await apiClient.post<T>(endpoint, body);
  return data;
}

export async function apiPut<T>(endpoint: string, body: any): Promise<T> {
  const { data } = await apiClient.put<T>(endpoint, body);
  return data;
}

export async function apiDelete<T>(endpoint: string): Promise<T> {
  const { data } = await apiClient.delete<T>(endpoint);
  return data;
}

export async function apiPatch<T>(endpoint: string, body: any): Promise<T> {
  const { data } = await apiClient.patch<T>(endpoint, body);
  return data;
}
